/** Session-scoped undo/redo for plain Text Asset inline editing.
 * Groups rapid keystrokes into one history entry (debounced snapshot),
 * caps stack size, and clears redo on new edits after undo. */

import { useCallback, useEffect, useRef, useState } from 'react'

const DEFAULT_MAX_ENTRIES = 80
const DEFAULT_GROUP_MS = 800

export function useTextEditHistory(
  externalText: string,
  options?: { maxEntries?: number; groupMs?: number },
) {
  const maxEntries = options?.maxEntries ?? DEFAULT_MAX_ENTRIES
  const groupMs = options?.groupMs ?? DEFAULT_GROUP_MS

  const [text, setTextState] = useState(externalText)
  const pastRef = useRef<string[]>([])
  const futureRef = useRef<string[]>([])
  const lastSnapshotRef = useRef(externalText)
  const groupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const focusedRef = useRef(false)
  const [, bump] = useState(0)
  const forceUpdate = useCallback(() => bump((n) => n + 1), [])

  useEffect(() => {
    if (focusedRef.current) return
    setTextState(externalText)
    lastSnapshotRef.current = externalText
    pastRef.current = []
    futureRef.current = []
    forceUpdate()
  }, [externalText, forceUpdate])

  useEffect(
    () => () => {
      if (groupTimerRef.current) clearTimeout(groupTimerRef.current)
    },
    [],
  )

  const flushHistoryGroup = useCallback(() => {
    if (groupTimerRef.current) {
      clearTimeout(groupTimerRef.current)
      groupTimerRef.current = null
    }
    const snapshot = lastSnapshotRef.current
    setTextState((current) => {
      if (current === snapshot) return current
      const nextPast = [...pastRef.current, snapshot]
      if (nextPast.length > maxEntries) nextPast.shift()
      pastRef.current = nextPast
      lastSnapshotRef.current = current
      futureRef.current = []
      forceUpdate()
      return current
    })
  }, [forceUpdate, maxEntries])

  const setText = useCallback(
    (next: string) => {
      setTextState(next)
      futureRef.current = []
      if (groupTimerRef.current) clearTimeout(groupTimerRef.current)
      groupTimerRef.current = setTimeout(() => flushHistoryGroup(), groupMs)
      forceUpdate()
    },
    [flushHistoryGroup, groupMs, forceUpdate],
  )

  const undo = useCallback((): string | null => {
    flushHistoryGroup()
    const past = pastRef.current
    if (past.length === 0) return null
    const prev = past.pop()!
    futureRef.current = [...futureRef.current, text]
    lastSnapshotRef.current = prev
    setTextState(prev)
    forceUpdate()
    return prev
  }, [flushHistoryGroup, forceUpdate, text])

  const redo = useCallback((): string | null => {
    flushHistoryGroup()
    const future = futureRef.current
    if (future.length === 0) return null
    const next = future.pop()!
    pastRef.current = [...pastRef.current, text]
    lastSnapshotRef.current = next
    setTextState(next)
    forceUpdate()
    return next
  }, [flushHistoryGroup, forceUpdate, text])

  const canUndo = pastRef.current.length > 0 || text !== lastSnapshotRef.current
  const canRedo = futureRef.current.length > 0

  const onFocus = useCallback(() => {
    focusedRef.current = true
  }, [])

  const onBlur = useCallback(() => {
    focusedRef.current = false
    flushHistoryGroup()
  }, [flushHistoryGroup])

  const resetHistory = useCallback(
    (next: string) => {
      if (groupTimerRef.current) clearTimeout(groupTimerRef.current)
      groupTimerRef.current = null
      pastRef.current = []
      futureRef.current = []
      lastSnapshotRef.current = next
      setTextState(next)
      forceUpdate()
    },
    [forceUpdate],
  )

  return {
    text,
    setText,
    undo,
    redo,
    canUndo,
    canRedo,
    onFocus,
    onBlur,
    flushHistoryGroup,
    resetHistory,
    maxEntries,
    groupMs,
  }
}
